import { useEffect, useRef } from 'react'
import type { DailyParticipant } from '@daily-co/daily-js'

import { attachVideoTrack } from '@/hooks/use-daily-call'
import { cn } from '@/lib/utils'

function VideoTile({
  participant,
  label,
  className,
  waiting,
}: {
  participant?: DailyParticipant
  label: string
  className?: string
  waiting?: boolean
}) {
  const ref = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    attachVideoTrack(ref.current, participant)
  }, [participant, participant?.tracks.video?.persistentTrack])

  const videoOff = waiting || !participant?.tracks.video?.persistentTrack

  return (
    <div className={cn('relative overflow-hidden bg-muted', className)}>
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={Boolean(participant?.local)}
        className={cn('size-full object-cover', videoOff && 'invisible')}
      />
      {videoOff ? (
        <p className="absolute inset-0 flex items-center justify-center px-3 text-center text-sm text-muted-foreground">
          {waiting ? 'Waiting for the other person…' : 'Camera off'}
        </p>
      ) : null}
      <span className="absolute bottom-2 left-2 rounded-md bg-background/80 px-2 py-0.5 text-xs">
        {label}
      </span>
    </div>
  )
}

/**
 * Featured remote (candidate / other person) with local self-view as picture-in-picture.
 */
export function DailyVideoTiles({
  participants,
  localLabel = 'You',
  remoteLabel = 'Guest',
}: {
  participants: DailyParticipant[]
  localLabel?: string
  remoteLabel?: string
}) {
  const local = participants.find((p) => p.local)
  const remotes = participants.filter((p) => !p.local)
  const featured = remotes[0]
  const extraRemotes = remotes.slice(1)

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl border bg-muted">
      <VideoTile
        participant={featured}
        label={featured?.user_name || remoteLabel}
        waiting={!featured}
        className="size-full"
      />
      {local ? (
        <VideoTile
          participant={local}
          label={localLabel}
          className="absolute bottom-3 right-3 z-10 aspect-video w-[26%] min-w-[7.5rem] max-w-[13rem] rounded-lg shadow-lg ring-1 ring-border"
        />
      ) : null}
      {extraRemotes.length > 0 ? (
        <div className="absolute bottom-3 left-3 z-10 flex max-w-[40%] gap-2">
          {extraRemotes.map((p) => (
            <VideoTile
              key={p.session_id}
              participant={p}
              label={p.user_name || remoteLabel}
              className="aspect-video w-28 overflow-hidden rounded-lg shadow-lg ring-1 ring-border"
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
